<template>
    <transition name="layout-fade" mode="out-in">
    <div :key="layout" :class="{ 'compact-layout': isCompact }">
        <!-- Entities Selection, Drug and Patient Information -->

        <!-- Compact layout: Store and Patient side by side -->
        <b-row v-if="isCompact">
            <b-col>
                <div class="compact-search-label">
                    <label>Store: <span class="text-danger">*</span></label>
                    <HelpPopup v-if="localStore" icon="fa fa-info-circle" placement="bottom"
                        popover-custom-class="compact-details-popover" :tabindex="-1">
                        <div>
                            <strong>Location:</strong> {{ formState.storeLocationInformation }}<br />
                            <strong>Phone:</strong> {{ localStore && localStore.phoneNumber | formatPhone }}<br />
                            <strong>NPI:</strong> {{ localStore && localStore.npiNumber }}
                        </div>
                    </HelpPopup>
                </div>
                <SearchComponent :id="localStore && +localStore.id" v-model="localStore"
                    :disabled="formState.shouldLockEscriptProcessingStore" label="Store" prepopulate :hide-label="true"
                    search-u-r-l="/Store" :params="formState.searchStoreGetParams"
                    :autoselect="formState.newMode || formState.fromIngestion ? formState.autoselectStore : undefined" required />
            </b-col>
            <b-col>
                <div class="compact-search-label">
                    <label>Patient: <span class="text-danger">*</span></label>
                    <a href="#" class="compact-action-link" tabindex="-1" @click.prevent="formState.startEditPatient"><i class="fas fa-edit mr-1"></i>Edit</a>
                    <a v-if="localPatient && localPatient.id" href="#" class="compact-action-link"
                        tabindex="-1" @click.prevent="formState.openRxHistory"><i class="fas fa-history mr-1"></i>RX History</a>
                    <HelpPopup v-if="localPatient" icon="fa fa-info-circle" placement="bottom"
                        popover-custom-class="compact-details-popover" :tabindex="-1">
                        <div>
                            <strong>Phone:</strong> {{ localPatient && localPatient.phoneDisplay }}
                            <PhoneCopyButton v-if="localPatient?.phoneNumber?.length > 0" :phone="localPatient.phoneNumber" />
                            <br />
                            <strong>Cell:</strong> {{ localPatient && localPatient.cellDisplay }}
                            <PhoneCopyButton v-if="localPatient?.cellNumber?.length > 0" :phone="localPatient.cellNumber" />
                            <br />
                            <strong>DOB:</strong> {{ formState.patientDOB }}
                        </div>
                    </HelpPopup>
                </div>
                <SearchComponent ref="patient-search-component" :id="localPatient && +localPatient.id"
                    v-model="localPatient" create-new-object createNewLabel="Create New Patient"
                    :disabled="formState.disableFields || formState.disablePatient" label="Patient" :hide-label="true"
                    search-u-r-l="/Patient"
                    required @createNewObject="formState.onShowPatientForm" @input="formState.patientUpdate" />
            </b-col>
        </b-row>

        <!-- Default layout: Store row with details -->
        <b-row v-if="!isCompact">
            <b-col>
                <SearchComponent :id="localStore && +localStore.id" v-model="localStore"
                    :disabled="formState.shouldLockEscriptProcessingStore" label="Store" prepopulate
                    search-u-r-l="/Store" :params="formState.searchStoreGetParams"
                    :autoselect="formState.newMode || formState.fromIngestion ? formState.autoselectStore : undefined" required />
            </b-col>
            <b-col>
                <b-row>
                    <b-col>
                        <b-row>
                            <b-col>
                                <strong>Store Location:</strong>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                {{ formState.storeLocationInformation }}
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col>
                        <b-row>
                            <b-col>
                                <strong>Store Phone:</strong>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                {{ localStore && localStore.phoneNumber | formatPhone }}
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <strong>NPI:</strong>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        {{ localStore && localStore.npiNumber }}
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <hr v-if="!isCompact" />
        <!-- Default layout: Patient row with details -->
        <b-row v-if="!isCompact">
            <b-col>
                <SearchComponent ref="patient-search-component" :id="localPatient && +localPatient.id"
                    v-model="localPatient" create-new-object createNewLabel="Create New Patient"
                    :disabled="formState.disableFields || formState.disablePatient" label="Patient" search-u-r-l="/Patient"
                    required @createNewObject="formState.onShowPatientForm" @input="formState.patientUpdate" />
                <b-button size="sm" variant="outline-primary" @click="formState.startEditPatient">
                    Edit Patient
                </b-button>
                <b-button :disabled="!(localPatient && localPatient.id)" size="sm" variant="outline-primary"
                    @click="formState.openRxHistory">
                    Show RX History
                </b-button>
            </b-col>
            <b-col>
                <b-row>
                    <b-col>
                        <strong>Patient Phone:</strong>
                        <br />
                        {{ localPatient && localPatient.phoneDisplay }}
                        <PhoneCopyButton v-if="localPatient?.phoneNumber?.length > 0" :phone="localPatient.phoneNumber" />
                        <br />
                        <strong>Patient Cell:</strong>
                        <br />
                        {{ localPatient && localPatient.cellDisplay }}
                        <PhoneCopyButton v-if="localPatient?.cellNumber?.length > 0" :phone="localPatient.cellNumber" />

                    </b-col>
                    <b-col>
                        <strong>DOB:</strong>
                        <br />
                        {{ formState.patientDOB }}
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <hr v-if="!isCompact" />
        <!-- Compact layout: Doctor with inline popover -->
        <b-row v-if="isCompact">
            <b-col>
                <div class="compact-search-label">
                    <label>Doctor: <span class="text-danger">*</span></label>
                    <a href="#" class="compact-action-link" tabindex="-1" @click.prevent="formState.onEditDoctorForm"><i class="fas fa-edit mr-1"></i>Edit</a>
                    <a v-if="localDoctor?.contacts?.length" id="office-contacts-popover-compact" href="#"
                        class="compact-action-link" tabindex="-1" @click.prevent><i class="fas fa-address-book mr-1"></i>Contacts</a>
                    <HelpPopup v-if="localDoctor" icon="fa fa-info-circle" placement="bottom"
                        popover-custom-class="compact-details-popover" :tabindex="-1">
                        <div>
                            <strong>Phone:</strong> {{ localDoctor?.phoneNumber | formatPhone }}
                            <PhoneCopyButton v-if="localDoctor?.phoneNumber?.length > 0" :phone="localDoctor.phoneNumber" />
                            <br />
                            <strong>Fax:</strong> {{ localDoctor?.faxNumber | formatPhone }}
                            <a v-if="localDoctor?.faxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                <i class="fas fa-copy" @click.prevent="copy(localDoctor.faxNumber)" />
                            </a>
                            <template v-if="localDoctor?.altNumber">
                                <br />
                                <strong>Alt Phone:</strong> {{ localDoctor?.altNumber | formatPhone }}
                                <PhoneCopyButton v-if="localDoctor?.altNumber?.length > 0" :phone="localDoctor.altNumber" />
                            </template>
                            <template v-if="localDoctor?.altFaxNumber">
                                <br />
                                <strong>Alt Fax:</strong> {{ localDoctor?.altFaxNumber | formatPhone }}
                                <a v-if="localDoctor?.altFaxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                    <i class="fas fa-copy" @click.prevent="copy(localDoctor.altFaxNumber)" />
                                </a>
                            </template>
                            <br />
                            <strong>Address:</strong><br />
                            <template v-if="localDoctor?.addressCity">
                                <span v-if="localDoctor.address1">{{ localDoctor.address1 }}, </span>
                                <span v-if="localDoctor.address2">{{ localDoctor.address2 }}, </span>
                                {{ [localDoctor.addressCity?.trim(), localDoctor.addressState?.trim()].join(", ") }}
                                {{ localDoctor.addressZip }}
                            </template>
                            <br />
                            <span :class="{ 'text-danger': formState.isNPIInvalid }">
                                <strong>NPI:</strong> {{ localDoctor?.npiNumber }}
                            </span>
                            <template v-if="localDoctor?.deaNumber">
                                <br />
                                <strong>DEA:</strong> {{ localDoctor.deaNumber }}
                            </template>
                            <template v-if="formState.doctorProgram.id">
                                <br />
                                <strong>Program:</strong> {{ formState.doctorProgram.name }}
                            </template>
                            <template v-if="effectiveAccountManagerName">
                                <br />
                                <strong>Account Manager:</strong> {{ effectiveAccountManagerName }}
                            </template>
                        </div>
                    </HelpPopup>
                </div>
                <div class="compact-tight-spacing">
                    <SearchComponent ref="prescriber-search-component" :id="localDoctor && +localDoctor.id"
                        v-model="localDoctor" create-new-object createNewLabel="Create New Doctor"
                        :disabled="formState.disableFields" label="Doctor" :hide-label="true" search-u-r-l="/Prescriber" required
                        :static-options="formState.patientPrescribers"
                        :results-modifier="formState.prescriberResultsModifier"
                        :paramsInGetById="{ includeProgramAccountManagers: true }"
                        @createNewObject="formState.onShowDoctorForm" @input="formState.doctorUpdate" />
                </div>
                <b-popover v-if="localDoctor?.contacts?.length" target="office-contacts-popover-compact"
                    triggers="click blur" custom-class="office-contacts-popover">
                    <b-card v-for="c in localDoctor.contacts" :key="c.id" style="box-shadow: none;">
                        <strong>{{ c.firstName }} {{ c.lastName }}</strong>
                        <div>{{ c.title }}</div>
                        <div>
                            {{ c.phoneNumber | formatPhone }}
                            <PhoneCopyButton :phone="c.phoneNumber" />
                        </div>
                        <div>
                            <small class="text-muted">{{ c.note }}</small>
                        </div>
                    </b-card>
                </b-popover>
            </b-col>
            <!-- Compact layout: Drug (same row as Doctor) -->
            <b-col id="drug-search-col">
                <b-row v-show="formState.showDrugChange">
                    <b-col>
                        <strong class="text-danger">NDC Change from DrFirst SmartSig. Verification
                            required.</strong>
                    </b-col>
                </b-row>
                <div class="compact-search-label">
                    <label>Drug: <span class="text-danger">*</span></label>
                    <EquivalentDrugsComponent ref="equivalent-drugs-component"
                        v-show="localDrug && localDrug.drugId && !formState.disableFields && (localDrug.source === 0 || localDrug.source === 2)"
                        :closeEquivalentDrugList="true" :drug="formState.drugAsDrug"
                        class="compact-action-link" :tabindex="-1"
                        @drugsSelected="formState.equivalentDrugsSearched" />
                    <a v-if="isCompoundDrug" href="#" class="compact-action-link" tabindex="-1"
                        @click.prevent="showCompactIngredients = !showCompactIngredients"><i class="fa fa-flask mr-1"></i>Ingredients</a>
                    <router-link v-if="isCompoundDrug" class="compact-action-link" tabindex="-1"
                        :to="{ name: 'Compound-Drug', params: { id: String(localDrug.drugId) } }" target="_blank"><i class="fas fa-edit mr-1"></i>Edit</router-link>
                    <HelpPopup v-if="localDrug && localDrug.drugId" icon="fa fa-info-circle" placement="bottom"
                        boundary="viewport" popover-custom-class="compact-details-popover" :tabindex="-1">
                        <div>
                            <template v-if="formState.loadingDrugDetails">
                                <b-spinner small class="mr-2" label="Spinning" />
                                <strong>Loading product details...</strong>
                            </template>
                            <template v-else-if="formState.errorLoadingDrugDetails">
                                <strong class="text-danger">Error loading product details.</strong>
                            </template>
                            <template v-else-if="formState.drugDetails">
                                <strong>NDC:</strong> {{ formState.drugNdc }}<br />
                                <strong>Package Size:</strong> {{ formState.drugPackageSize }}<br />
                                <template v-if="formState.drugDetails.program">
                                    <strong>Drug Program:</strong> {{ formState.drugDetails.program.name }}<br />
                                </template>
                                <strong>Drug Status:</strong> {{ formState.drugStatus }}<br />
                                <template v-if="formState.drugLastFilled">
                                    <strong>Last Filled:</strong> {{ formState.drugLastFilled }}<br />
                                </template>
                                <strong v-if="!formState.drugInventory" class="text-warning">Unknown Stock</strong>
                                <strong v-if="formState.drugInStock" class="text-success">In Stock</strong>
                                <strong v-if="formState.drugOutOfStock" class="text-danger">Out Of Stock</strong>
                                <br />
                                <template v-if="formState.drugDetails.packageDescription">
                                    <strong>Package Description:</strong> {{ formState.drugDetails.packageDescription }}<br />
                                </template>
                                <template v-if="formState.imageURLs.length && !formState.imageError">
                                    <strong>Product Image:</strong><br />
                                    <div class="text-center">
                                        <b-img v-for="image of formState.imageURLs" :key="image" :src="image" class="mb-2"
                                            style="max-height: 150px" thumbnail fluid @load="formState.onImageErrorChange(false)"
                                            @error="formState.onImageErrorChange(true)" />
                                    </div>
                                </template>
                                <i v-else-if="!formState.imageURLs.length">No images found for this selected product.</i>
                            </template>
                        </div>
                        <CompoundIngredientsDisplay
                            v-if="formState.prescription && formState.prescription.drugSource === 2 && formState.drugDetails && formState.drugDetails.ingredients && formState.drugDetails.ingredients.length"
                            :ingredients="formState.drugDetails.ingredients"
                            :drug-id="localDrug.drugId"
                            :store-id="formState.prescription.storeID"
                            :rx-number="formState.prescription.rxNumber"
                            :rf-number="formState.prescription.rfNumber"
                            :compound-preset-id="formState.prescription.compoundPresetID"
                            :is-billed="hasActiveClaims"
                            @preset-changed="onCompoundPresetChanged" />
                    </HelpPopup>
                </div>
                <SearchPrescriptionDetailComponent ref="drug-search-component" :id="localDrug && localDrug.drugId"
                    :disabled="formState.disableFields" v-model="localDrug" label="Drug" :hide-label="true"
                    :params="formState.prescriptionDrugSearchParams"
                    :paramsInGetById="formState.drugSearchParams" :equivalent-drugs="formState.equivalentDrugs"
                    search-u-r-l="/Drug" required />
                <CompoundIngredientsDisplay v-if="showCompactIngredients && isCompoundDrug"
                    :ingredients="formState.drugDetails.ingredients"
                    :drug-id="localDrug.drugId"
                    :store-id="formState.prescription.storeID"
                    :rx-number="formState.prescription.rxNumber"
                    :rf-number="formState.prescription.rfNumber"
                    :compound-preset-id="formState.prescription.compoundPresetID"
                    :is-billed="hasActiveClaims"
                    :inline="true"
                    @preset-changed="onCompoundPresetChanged" />
            </b-col>
        </b-row>
        <b-row v-if="isCompact">
            <b-col>
                <SearchComponent :id="localProgram && +localProgram.id" ref="program-search-component"
                    v-model="localProgram" :autoselect="formState.autoselectProgram" clearable
                    :disabled="formState.disableProgramChange" label="Program"
                    :params="formState.searchProgramGetParams" :paramsInGetById="{ includeFlags: true }"
                    prepopulate search-u-r-l="/Program" required @input="formState.programUpdate" />
            </b-col>
        </b-row>
        <b-row v-if="isCompact && showSupervisingPhysician">
            <b-col>
                <div class="compact-search-label">
                    <label>Supervising Physician: <span class="text-danger">*</span></label>
                    <a href="#" class="compact-action-link" tabindex="-1" @click.prevent="formState.onEditSupervisingPhysicianForm"><i class="fas fa-edit mr-1"></i>Edit</a>
                    <HelpPopup v-if="localSupervisingPhysician?.id" icon="fa fa-info-circle" placement="bottom"
                        popover-custom-class="compact-details-popover" :tabindex="-1">
                        <div>
                            <strong>Phone:</strong> {{ localSupervisingPhysician?.phoneNumber | formatPhone }}
                            <PhoneCopyButton v-if="localSupervisingPhysician?.phoneNumber?.length > 0" :phone="localSupervisingPhysician.phoneNumber" />
                            <br />
                            <strong>Fax:</strong> {{ localSupervisingPhysician?.faxNumber | formatPhone }}
                            <a v-if="localSupervisingPhysician?.faxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                <i class="fas fa-copy" @click.prevent="copy(localSupervisingPhysician.faxNumber)" />
                            </a>
                            <template v-if="localSupervisingPhysician?.altNumber">
                                <br />
                                <strong>Alt Phone:</strong> {{ localSupervisingPhysician?.altNumber | formatPhone }}
                                <PhoneCopyButton v-if="localSupervisingPhysician?.altNumber?.length > 0" :phone="localSupervisingPhysician.altNumber" />
                            </template>
                            <template v-if="localSupervisingPhysician?.altFaxNumber">
                                <br />
                                <strong>Alt Fax:</strong> {{ localSupervisingPhysician?.altFaxNumber | formatPhone }}
                                <a v-if="localSupervisingPhysician?.altFaxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                    <i class="fas fa-copy" @click.prevent="copy(localSupervisingPhysician.altFaxNumber)" />
                                </a>
                            </template>
                            <br />
                            <strong>Address:</strong><br />
                            <template v-if="localSupervisingPhysician?.addressCity">
                                <span v-if="localSupervisingPhysician.address1">{{ localSupervisingPhysician.address1 }}, </span>
                                <span v-if="localSupervisingPhysician.address2">{{ localSupervisingPhysician.address2 }}, </span>
                                {{ [localSupervisingPhysician.addressCity?.trim(), localSupervisingPhysician.addressState?.trim()].join(", ") }}
                                {{ localSupervisingPhysician.addressZip }}
                            </template>
                            <br />
                            <strong>NPI:</strong> {{ localSupervisingPhysician?.npiNumber }}
                            <template v-if="localSupervisingPhysician?.deaNumber">
                                <br />
                                <strong>DEA:</strong> {{ localSupervisingPhysician.deaNumber }}
                            </template>
                        </div>
                    </HelpPopup>
                </div>
                <div class="compact-tight-spacing">
                    <SearchComponent :id="localSupervisingPhysicianId"
                        v-model="localSupervisingPhysician"
                        create-new-object createNewLabel="Create New Supervising Physician"
                        search-u-r-l="/Prescriber"
                        :params="{ excludeRequiresSupervisingPhysician: true }"
                        :disabled="formState.disableFields"
                        :clearable="true"
                        :hide-label="true"
                        required
                        :option-label="supervisingPhysicianOptionLabel"
                        @createNewObject="formState.onShowSupervisingPhysicianForm"
                        @input="formState.supervisingPhysicianUpdate" />
                </div>
            </b-col>
        </b-row>
        <!-- Default layout: Doctor row with details -->
        <b-row v-if="!isCompact">
            <b-col>
                <SearchComponent ref="prescriber-search-component" :id="localDoctor && +localDoctor.id"
                    v-model="localDoctor" create-new-object createNewLabel="Create New Doctor"
                    :disabled="formState.disableFields" label="Doctor" search-u-r-l="/Prescriber" required
                    :static-options="formState.patientPrescribers"
                    :results-modifier="formState.prescriberResultsModifier"
                    :paramsInGetById="{ includeProgramAccountManagers: true }"
                    @createNewObject="formState.onShowDoctorForm" @input="formState.doctorUpdate" />
                <b-button size="sm" variant="outline-primary" @click="formState.onEditDoctorForm">
                    Edit Doctor
                </b-button>
                <NpiValidationBadge v-if="localDoctor?.id" :prescriber-id="localDoctor.id" />
                <template v-if="localDoctor?.contacts?.length">
                    <b-button id="office-contacts-popover" size="sm" variant="outline-primary">
                        View Office Contacts
                    </b-button>
                    <b-popover target="office-contacts-popover" triggers="click blur"
                        custom-class="office-contacts-popover">
                        <b-card v-for="c in localDoctor.contacts" :key="c.id" style="box-shadow: none;">
                            <strong>{{ c.firstName }} {{ c.lastName }}</strong>
                            <div>
                                {{ c.title }}
                            </div>
                            <div>
                                {{ c.phoneNumber | formatPhone }}
                                <PhoneCopyButton :phone="c.phoneNumber" />
                            </div>
                            <div>
                                <small class="text-muted">
                                    {{ c.note }}
                                </small>
                            </div>
                        </b-card>
                    </b-popover>
                </template>
            </b-col>
            <b-col>
                <b-row>
                    <b-col>
                        <strong>Prescriber Phone:</strong>
                        <br />
                        {{ localDoctor?.phoneNumber | formatPhone }}
                        <PhoneCopyButton v-if="localDoctor?.phoneNumber?.length > 0" :phone="localDoctor.phoneNumber" />
                        <br />
                        <strong>Prescriber Fax:</strong>
                        <br />
                        {{ localDoctor?.faxNumber | formatPhone }}
                        <a v-if="localDoctor?.faxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                            <i class="fas fa-copy" @click.prevent="copy(localDoctor.faxNumber)" />
                        </a>
                        <br />
                        <strong>Prescriber Address:</strong>
                        <br />
                        <template v-if="localDoctor?.addressCity">
                            <div v-if="localDoctor.address1">{{ localDoctor.address1 }}</div>
                            <div v-if="localDoctor.address2">{{ localDoctor.address2 }}</div>
                            <div>
                                {{
                                    [localDoctor.addressCity?.trim(), localDoctor.addressState?.trim()].join(", ")
                                }}
                                {{ localDoctor.addressZip }}
                            </div>
                        </template>
                    </b-col>
                    <b-col>
                        <div v-if="localDoctor?.altNumber">
                            <strong>Alt Phone:</strong>
                            <br />
                            {{ localDoctor?.altNumber | formatPhone }}
                            <PhoneCopyButton v-if="localDoctor?.altNumber?.length > 0" :phone="localDoctor.altNumber" />
                        </div>
                        <div v-if="localDoctor?.altFaxNumber">
                            <strong>Alt Fax:</strong>
                            <br />
                            {{ localDoctor?.altFaxNumber | formatPhone }}
                            <a v-if="localDoctor?.altFaxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                <i class="fas fa-copy" @click.prevent="copy(localDoctor.altFaxNumber)" />
                            </a>
                        </div>
                        <div :class="{ 'text-danger': formState.isNPIInvalid }">
                            <strong>NPI:</strong>
                            <br />
                            {{ localDoctor?.npiNumber }}
                        </div>
                        <div v-if="formState.doctorProgram.id">
                            <strong>Prescriber Program:</strong><br />
                            {{ formState.doctorProgram.name }}
                        </div>
                        <div v-if="localDoctor?.deaNumber">
                            <strong>DEA:</strong>
                            <br />
                            {{ localDoctor.deaNumber }}
                        </div>
                        <div v-if="effectiveAccountManagerName">
                            <strong>Account Manager:</strong>
                            <br />
                            {{ effectiveAccountManagerName }}
                        </div>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row v-if="!isCompact && showSupervisingPhysician">
            <b-col>
                <SearchComponent :id="localSupervisingPhysicianId"
                    v-model="localSupervisingPhysician"
                    create-new-object createNewLabel="Create New Supervising Physician"
                    :disabled="formState.disableFields"
                    label="Supervising Physician"
                    search-u-r-l="/Prescriber"
                    :params="{ excludeRequiresSupervisingPhysician: true }"
                    required
                    :option-label="supervisingPhysicianOptionLabel"
                    @createNewObject="formState.onShowSupervisingPhysicianForm"
                    @input="formState.supervisingPhysicianUpdate" />
                <b-button size="sm" variant="outline-primary" @click="formState.onEditSupervisingPhysicianForm">
                    Edit Supervising Physician
                </b-button>
                <NpiValidationBadge v-if="localSupervisingPhysician?.id" :prescriber-id="localSupervisingPhysician.id" />
            </b-col>
            <b-col>
                <b-row>
                    <b-col>
                        <strong>Supervisor Phone:</strong>
                        <br />
                        {{ localSupervisingPhysician?.phoneNumber | formatPhone }}
                        <PhoneCopyButton v-if="localSupervisingPhysician?.phoneNumber?.length > 0" :phone="localSupervisingPhysician.phoneNumber" />
                        <br />
                        <strong>Supervisor Fax:</strong>
                        <br />
                        {{ localSupervisingPhysician?.faxNumber | formatPhone }}
                        <a v-if="localSupervisingPhysician?.faxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                            <i class="fas fa-copy" @click.prevent="copy(localSupervisingPhysician.faxNumber)" />
                        </a>
                        <br />
                        <strong>Supervisor Address:</strong>
                        <br />
                        <template v-if="localSupervisingPhysician?.addressCity">
                            <div v-if="localSupervisingPhysician.address1">{{ localSupervisingPhysician.address1 }}</div>
                            <div v-if="localSupervisingPhysician.address2">{{ localSupervisingPhysician.address2 }}</div>
                            <div>
                                {{
                                    [localSupervisingPhysician.addressCity?.trim(), localSupervisingPhysician.addressState?.trim()].join(", ")
                                }}
                                {{ localSupervisingPhysician.addressZip }}
                            </div>
                        </template>
                    </b-col>
                    <b-col>
                        <div v-if="localSupervisingPhysician?.altNumber">
                            <strong>Alt Phone:</strong>
                            <br />
                            {{ localSupervisingPhysician?.altNumber | formatPhone }}
                            <PhoneCopyButton v-if="localSupervisingPhysician?.altNumber?.length > 0" :phone="localSupervisingPhysician.altNumber" />
                        </div>
                        <div v-if="localSupervisingPhysician?.altFaxNumber">
                            <strong>Alt Fax:</strong>
                            <br />
                            {{ localSupervisingPhysician?.altFaxNumber | formatPhone }}
                            <a v-if="localSupervisingPhysician?.altFaxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                <i class="fas fa-copy" @click.prevent="copy(localSupervisingPhysician.altFaxNumber)" />
                            </a>
                        </div>
                        <div>
                            <strong>NPI:</strong>
                            <br />
                            {{ localSupervisingPhysician?.npiNumber }}
                        </div>
                        <div v-if="localSupervisingPhysician?.deaNumber">
                            <strong>DEA:</strong>
                            <br />
                            {{ localSupervisingPhysician.deaNumber }}
                        </div>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <hr v-if="!isCompact" />
        <b-row v-if="!isCompact" v-show="formState.showDrugChange">
            <b-col>
                <strong class="text-danger">NDC Change from DrFirst SmartSig. Verification
                    required.</strong>
            </b-col>
        </b-row>
        <!-- Default layout: Drug row with details -->
        <b-row v-if="!isCompact" id="drug-row">
            <b-col id="drug-search-col">
                <SearchPrescriptionDetailComponent ref="drug-search-component" :id="localDrug && localDrug.drugId"
                    :disabled="formState.disableFields" v-model="localDrug" label="Drug"
                    :params="formState.prescriptionDrugSearchParams"
                    :paramsInGetById="formState.drugSearchParams" :equivalent-drugs="formState.equivalentDrugs"
                    search-u-r-l="/Drug" required>
                </SearchPrescriptionDetailComponent>
                <EquivalentDrugsComponent ref="equivalent-drugs-component"
                    v-show="localDrug && localDrug.drugId && !formState.disableFields && (localDrug.source === 0 || localDrug.source === 2)"
                    :closeEquivalentDrugList="true" :drug="formState.drugAsDrug"
                    @drugsSelected="formState.equivalentDrugsSearched" />
                <br>
                <SearchComponent :id="localProgram && +localProgram.id" ref="program-search-component"
                    v-model="localProgram" :autoselect="formState.autoselectProgram" clearable
                    :disabled="formState.disableProgramChange" label="Program"
                    :params="formState.searchProgramGetParams" :paramsInGetById="{ includeFlags: true }"
                    prepopulate search-u-r-l="/Program" required @input="formState.programUpdate" />
            </b-col>
            <b-col id="drug-details-col">
                <!-- Drug Details-->
                <b-row v-if="!!formState.drugDetails">
                    <!-- Drug information-->
                    <b-col cols="12">
                        <b-row>
                            <b-col>
                                <strong>NDC:</strong>
                                {{ formState.drugNdc }}
                                <br />
                                <strong>Package Size:</strong>
                                {{ formState.drugPackageSize }}
                                <template v-if="formState.drugDetails.program">
                                    <br />
                                    <strong>Drug Program:</strong>
                                    {{ formState.drugDetails.program.name }}
                                </template>
                            </b-col>
                            <b-col>
                                <strong>Drug Status:</strong>
                                {{ formState.drugStatus }}
                                <template v-if="formState.drugLastFilled">
                                    <br />
                                    <strong>Last Filled:</strong>
                                    {{ formState.drugLastFilled }}
                                </template>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <strong v-show="!formState.drugInventory" class="text-warning">
                                    Unknown Stock
                                </strong>
                                <strong v-show="formState.drugInStock" class="text-success">
                                    In Stock
                                </strong>
                                <strong v-show="formState.drugOutOfStock" class="text-danger">
                                    Out Of Stock
                                </strong>
                            </b-col>
                        </b-row>
                        <b-row v-if="formState.drugDetails && formState.drugDetails.packageDescription">
                            <b-col>
                                <strong>Package Description:</strong><br>
                                {{ formState.drugDetails.packageDescription }}
                            </b-col>
                        </b-row>
                        <!-- Compound Ingredients -->
                        <CompoundIngredientsDisplay
                            v-if="formState.prescription && formState.prescription.drugSource === 2 && formState.drugDetails && formState.drugDetails.ingredients && formState.drugDetails.ingredients.length"
                            :ingredients="formState.drugDetails.ingredients"
                            :drug-id="localDrug.drugId"
                            :store-id="formState.prescription.storeID"
                            :rx-number="formState.prescription.rxNumber"
                            :rf-number="formState.prescription.rfNumber"
                            :compound-preset-id="formState.prescription.compoundPresetID"
                            :is-billed="hasActiveClaims"
                            @preset-changed="onCompoundPresetChanged" />
                    </b-col>

                    <!-- Drug Images--->
                    <b-col cols="12" v-show="!formState.imageError">
                        <div v-if="formState.imageURLs.length">
                            <strong>Product Image:</strong>
                            <br />
                            <div class="text-center">
                                <b-img v-for="image of formState.imageURLs" :key="image" :src="image" class="mb-2"
                                    style="max-height: 150px" thumbnail fluid @load="formState.onImageErrorChange(false)"
                                    @error="formState.onImageErrorChange(true)" />
                            </div>
                        </div>
                        <i v-else-if="!!formState.drugDetails">No images found for this selected product.</i>
                    </b-col>
                </b-row>
                <b-row v-else-if="formState.loadingDrugDetails">
                    <b-col>
                        <b-spinner class="mr-2" label="Spinning" />
                        <strong>Loading product details...</strong>
                    </b-col>
                </b-row>
                <b-row v-else-if="formState.errorLoadingDrugDetails && !formState.loadingDrugDetails">
                    <b-col>
                        <strong style="color: red">Error Loading product details.</strong>
                        <span>Try searching the drug again.</span>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>

        <hr v-if="!isCompact" />

        <!-- Quantities -->
        <b-row>
            <b-col>
                <fg-input v-model.number="formState.prescription.quantity" :class="formState.validationClasses.quantity"
                    :disabled="formState.disableQuantity" :no-wheel="true" label="Quantity" min="0" required
                    type="number" @input="formState.changeDispense" />
            </b-col>
            <b-col>
                <fg-input v-model.number="localDispensedQuantity" :class="formState.validationClasses.dispense"
                    :disabled="formState.disableFields" label="Dispensed" min="0" required type="number"
                    @input="formState.changeDispense" />
            </b-col>
            <b-col>
                <fg-input v-if="!formState.prescription || !formState.prescription.rfNumber"
                    v-model.number="formState.prescription.refills" :class="formState.validationClasses.refills"
                    :disabled="formState.disableRefills" label="Refills" min="0" required type="number" />
                <fg-input v-else v-model.number="formState.fillsLeft" :disabled="true" label="Refills" min="0"
                    required type="number" />
            </b-col>
            <b-col>
                <fg-input v-model.number="formState.prescription.daySupply" :class="formState.validationClasses.daySupply"
                    :disabled="formState.disableFields" label="Days' Supply" min="0" required type="number" />
            </b-col>
        </b-row>
        <!-- Compact layout: Directions and Rx Memo side by side -->
        <b-row v-if="isCompact">
            <b-col>
                <DirectionsComponent ref="directions-component" v-model="formState.prescription.directions"
                    :disabled="formState.disableBillingComponent"
                    :program-id="formState.prescription.programID"
                    :dispensed-amount="formState.prescription.dispensed"
                    @days-supply-calculated="formState.handleDaysSupplyCalculated" />
            </b-col>
            <b-col>
                <fg-input label="Rx Memo">
                    <textarea v-model="formState.prescription.memo" class="textarea-autosize form-control" />
                </fg-input>
            </b-col>
        </b-row>

        <!-- Default layout: Directions and Rx Memo in separate rows -->
        <b-row v-if="!isCompact">
            <b-col>
                <DirectionsComponent ref="directions-component" v-model="formState.prescription.directions"
                    :disabled="formState.disableBillingComponent"
                    :program-id="formState.prescription.programID"
                    :dispensed-amount="formState.prescription.dispensed"
                    @days-supply-calculated="formState.handleDaysSupplyCalculated" />
            </b-col>
        </b-row>

        <b-overlay :show="formState.loadingDirections" rounded="sm">
            <card v-if="formState.directionsTranslations?.paraphrases?.length" plain>
                <b-form-group label="Directions to be translated:" label-class="font-weight-bold">
                    <b-form-checkbox v-for="phrase in formState.directionsTranslations?.paraphrases"
                        :key="phrase.id" :value="phrase.id" :checked="formState.selectedTranslation"
                        @input="formState.onSelectedTranslationChange($event)">
                        {{ phrase.token }}
                    </b-form-checkbox>
                </b-form-group>
                <br>
                <b-button variant="info" size="sm" @click="formState.translateDirections">
                    Translate
                </b-button>
            </card>
        </b-overlay>

        <b-alert :show="formState.sigIsSmartSig" variant="danger">
            <strong class="d-flex" style="gap: 7px;">
                <i class="fa fa-warning"></i>
                Sig interpreted from DrFirst SmartSig.
                Verification required.
            </strong>
        </b-alert>

        <b-row v-if="!isCompact">
            <b-col>
                <fg-input label="Rx Memo">
                    <textarea v-model="formState.prescription.memo" class="textarea-autosize form-control" />
                </fg-input>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <EnumSelector ref="origin-selector" v-model="localOriginCode" :disabled="formState.disableFields"
                    :enum-codes="formState.originCodes" label="Origin" required />
            </b-col>
            <b-col>
                <EnumSelector ref="daw-selector" v-model="localDawCode" :disabled="formState.disableFields"
                    :enum-codes="formState.dawCodes" label="DAW" required />
            </b-col>
        </b-row>
        <b-row v-show="localOriginCode == formState.originCodes.Transfer">
            <b-col>
                <search-component v-model="localOriginPharmacy" :disabled="formState.disableFields"
                    search-u-r-l="/StoredPharmacy" label="Origin Pharmacy" create-new-label="Create New"
                    create-new-object clearable @createNewObject="formState.onShowPharmacyForm"
                    @input="onOriginPharmacyInput" />
            </b-col>
            <b-col></b-col>
        </b-row>
        <b-row>
            <b-col>
                <EnumSelector v-model="localTreatmentTypeCode" :disabled="formState.disableFields"
                    :enum-codes="formState.treatmentTypeCodes" label="Treatment Type" clearable />
            </b-col>
            <b-col></b-col>
        </b-row>
        <b-row>
            <b-col>
                <fg-input label="Fill Date" required>
                    <el-date-picker v-model="formState.prescription.fillDate" :class="formState.validationClasses.fillDate"
                        :disabled="formState.disableFields || formState.rxWasBilled" format="MM/dd/yyyy" type="date">
                    </el-date-picker>
                    <small v-if="formState.prescription.effectiveDate" class="text-muted d-block">
                        Effective {{ formState.prescription.effectiveDate | formatDate('L') }}
                    </small>
                </fg-input>
            </b-col>
            <b-col>
                <fg-input :disabled="formState.disableFields" label="Written" required>
                    <el-date-picker v-model="localWrittenDate" :class="formState.validationClasses.writtenDate"
                        :disabled="formState.disableFields" format="MM/dd/yyyy" type="date" />
                </fg-input>
            </b-col>
            <b-col>
                <fg-input label="Expires" required>
                    <el-date-picker v-model="localExpiration" :class="formState.validationClasses.expirationDate"
                        :disabled="formState.disableFields" format="MM/dd/yyyy" type="date" />
                </fg-input>
            </b-col>
            <b-col v-if="formState.showDispensingFields">
                <fg-input v-model="formState.prescription.partnerFillNumber" :disabled="formState.disableBillingComponent"
                    label="Partner Fill #" />
            </b-col>
        </b-row>
        <b-row align-v="end" v-if="formState.showDispensingFields">
            <b-col>
                <b-table :items="formState.dispenseItems"
                    :fields="['lotNumber', 'serialNumber', 'expirationDate', 'quantity']"
                    class="dispense-items" show-empty empty-text="No Dispense Items on Prescription">
                    <template #cell(lotNumber)="{ value, index }">
                        <fg-input :value="value" :disabled="formState.disableFields"
                            @input="formState.updateDispenseItem('lotNumber', index, $event)" class="m-0" />
                    </template>
                    <template #cell(serialNumber)="{ value, index }">
                        <fg-input :value="value" :disabled="formState.disableFields"
                            @input="formState.updateDispenseItem('serialNumber', index, $event)" class="m-0" />
                    </template>
                    <template #cell(expirationDate)="{ value, index }">
                        <fg-input class="m-0">
                            <el-date-picker :value="value" :disabled="formState.disableFields" format="MM/dd/yyyy"
                                type="date"
                                @input="formState.updateDispenseItem('expirationDate', index, $event)" />
                        </fg-input>
                    </template>
                    <template #cell(quantity)="{ value, index }">
                        <fg-input :value="value" :disabled="formState.disableFields" type="number"
                            @input="formState.updateDispenseItem('quantity', index, $event)" class="m-0" />
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-row>
                    <b-col cols="6">
                        <EnumSelector v-model="localStatusCode" :disabled="formState.disableStatusFields"
                            :elementsToDiscard="formState.notSelectableStatus" :enum-codes="formState.statusCodes"
                            label="Status" required />
                    </b-col>
                    <b-col cols="6">
                        <EnumSelector v-model="localDeliveryCode" :clearable="true" :disabled="formState.disableFields"
                            :enum-codes="formState.deliveryCodes" label="Delivery" required />
                    </b-col>
                    <b-col
                        v-if="localStatusCode == formState.statusCodes.OnHold || localStatusCode == formState.statusCodes.Discontinued"
                        cols="6">
                        Substatus*
                        <v-select
                            :value="{ value: localSubstatusCode, label: formState.applicableSubStatuses.find(ss => ss.value == localSubstatusCode)?.label }"
                            :options="formState.applicableSubStatuses.filter(ss => ss.active)"
                            :disabled="formState.disableStatusFields" @input="localSubstatusCode = $event?.value"
                            :clearable="false" />
                    </b-col>
                    <b-col v-if="localStatusCode == formState.statusCodes.OnHold" cols="6">
                        <label>
                            Next Touch Date{{ formState.requireNextTouchDateOnHold ? '*' : '' }}
                        </label>
                        <b-form-input
                            type="datetime-local"
                            v-model="localNextTouchDateLocal"
                            :disabled="formState.disableStatusFields"
                            :min="formState.minNextTouchDate"
                        />
                        <b-form-checkbox
                            v-model="localNextTouchDateManualOverride"
                            :disabled="formState.disableStatusFields"
                            class="mt-1"
                            size="sm"
                        >
                            Set date manually
                            <HelpPopup icon="fa fa-question-circle" placement="top" :tabindex="-1">
                                <div>
                                    By default, the Next Touch Date is auto-calculated from the
                                    substatus offset when you select a substatus. Check this box
                                    to enter a custom date that won't be overwritten when the
                                    substatus changes.
                                </div>
                            </HelpPopup>
                        </b-form-checkbox>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row v-if="formState.prescription.transferMyRxId?.length && formState.transferMyRxStatus != 'canceled'">
            <b-col cols="auto">
                {{ formState.transferMyRxStatus }}
            </b-col>
            <b-col cols="auto"
                v-if="formState.transferMyRxStatus != 'accepted' && formState.transferMyRxStatus != 'rejected' && formState.transferMyRxStatus != 'canceled'">
                <b-button v-b-modal.cancel-reason-modal>Cancel Transfer</b-button>
            </b-col>
        </b-row>
    </div>
    </transition>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Ref, Watch } from "vue-property-decorator";
import { DatePicker } from "element-ui";
import PhoneFormatMixin from "@/mixins/PhoneFormatMixin";
import SearchComponent from "@/components/SearchComponent.vue";
import SearchPrescriptionDetailComponent from "@/components/Prescription/SearchPrescriptionDetailComponent.vue";
import EquivalentDrugsComponent from "@/components/Drug/EquivalentDrugsComponent.vue";
import CompoundIngredientsDisplay from "@/components/Compound/CompoundIngredientsDisplay.vue";
import DirectionsComponent from "@/components/Prescription/DirectionsComponent.vue";
import EnumSelector from "@/components/EnumSelector.vue";
import HelpPopup from "@/components/HelpPopup.vue";
import NpiValidationBadge from "@/components/Prescriber/NpiValidationBadge.vue";
import PhoneCopyButton from "@/components/PhoneCopyButton.vue";
import { PrescriptionFormState } from "@/models/PrescriptionFormState";
import { PharmacyRecord } from "@/models/Pharmacy";
import { Store } from "@/models/Store";
import { Patient } from "@/models/Patient";
import { Prescriber } from "@/models/Prescriber";
import { Program } from "@/models/Program";
import { DrugProduct } from "@/models/Drug/DrugProduct";

@Component({
    name: "PrescriptionFormFields",
    components: {
        SearchComponent,
        SearchPrescriptionDetailComponent,
        EquivalentDrugsComponent,
        CompoundIngredientsDisplay,
        DirectionsComponent,
        EnumSelector,
        HelpPopup,
        NpiValidationBadge,
        PhoneCopyButton,
        [DatePicker.name]: DatePicker,
    },
})
export default class PrescriptionFormFields extends Mixins(PhoneFormatMixin) {
    @Prop({ required: true }) formState!: PrescriptionFormState;
    @Prop({ default: "default" }) layout!: "default" | "compact";
    // Entity .sync props (full object replacement by SearchComponent v-model)
    @Prop({ required: true }) store!: Store;
    @Prop({ required: true }) patient!: Patient;
    @Prop({ required: true }) doctor!: Prescriber;
    @Prop({ default: null }) supervisingPhysician!: Prescriber | null;
    @Prop({ default: false }) enableSupervisingPhysician!: boolean;
    @Prop({ required: true }) program!: Program;
    @Prop({ required: true }) drug!: DrugProduct;
    // Primitive .sync props
    @Prop({ required: true }) originCode!: number;
    @Prop({ required: true }) statusCode!: number;
    @Prop({ required: true }) deliveryCode!: number;
    @Prop({ required: true }) dawCode!: number;
    @Prop() substatusCode!: number | null;
    @Prop() treatmentTypeCode!: number | null;
    @Prop({ required: true }) dispensedQuantity!: number;
    @Prop() expiration!: Date | null;
    @Prop() writtenDate!: Date | any;
    @Prop({ default: "" }) nextTouchDateLocal!: string;
    @Prop({ default: false }) nextTouchDateManualOverride!: boolean;
    @Prop({ default: false }) hasActiveClaims!: boolean;

    // Expose child component refs for parent access
    @Ref("patient-search-component") patientSearchComponent!: SearchComponent;
    @Ref("prescriber-search-component") prescriberSearchComponent!: SearchComponent;
    @Ref("program-search-component") programSearchComponent!: SearchComponent;
    @Ref("directions-component") directionsComponent!: DirectionsComponent;
    @Ref("origin-selector") originComponent!: EnumSelector;
    @Ref("daw-selector") dawComponent!: EnumSelector;
    @Ref("drug-search-component") drugSearchComponent!: SearchPrescriptionDetailComponent;
    @Ref("equivalent-drugs-component") equivalentDrugsComponent!: EquivalentDrugsComponent;

    mounted() {
        document.addEventListener("keydown", this.preventDefaultForHotkeys);
        document.addEventListener("keyup", this.handleHotkeys);
    }

    beforeDestroy() {
        document.removeEventListener("keydown", this.preventDefaultForHotkeys);
        document.removeEventListener("keyup", this.handleHotkeys);
    }

    preventDefaultForHotkeys(e: KeyboardEvent) {
        if (e.altKey && e.key === "p") e.preventDefault();
        if (e.altKey && e.key === "r") e.preventDefault();
        if (e.altKey && e.shiftKey && e.key === "D") e.preventDefault();
        if (e.altKey && e.key === "d") e.preventDefault();
        if (e.altKey && e.key === "m") e.preventDefault();
        if (e.altKey && e.key === "i") e.preventDefault();
        if (e.altKey && e.key === "o") e.preventDefault();
        if (e.altKey && e.key === "w") e.preventDefault();
    }

    handleHotkeys(e: KeyboardEvent) {
        if (e.altKey && e.key === "p") this.patientSearchComponent?.focus();
        if (e.altKey && e.key === "r") this.prescriberSearchComponent?.focus();
        if (e.altKey && e.shiftKey && e.key === "D") this.equivalentDrugsComponent?.findEquivalentDrugs();
        if (e.altKey && e.key === "d") this.drugSearchComponent?.focus();
        if (e.altKey && e.key === "m") this.programSearchComponent?.focus();
        if (e.altKey && e.key === "i") this.directionsComponent?.focus();
        if (e.altKey && e.key === "o") this.originComponent?.focus();
        if (e.altKey && e.key === "w") this.dawComponent?.focus();
    }

    onCompoundPresetChanged(presetId: number | null) {
        this.$emit("preset-changed", presetId);
    }

    // Local origin pharmacy tracks formState but allows local v-model binding
    private localOriginPharmacyValue: PharmacyRecord | null = null;

    @Watch("formState.originPharmacy", { immediate: true })
    onFormStateOriginPharmacyChanged(val: PharmacyRecord) {
        this.localOriginPharmacyValue = val;
    }

    get localOriginPharmacy(): PharmacyRecord {
        return this.localOriginPharmacyValue || this.formState.originPharmacy;
    }

    set localOriginPharmacy(val: PharmacyRecord) {
        this.localOriginPharmacyValue = val;
    }

    onOriginPharmacyInput(val: PharmacyRecord | null) {
        this.formState.onOriginPharmacyChange(val);
    }

    // Computed get/set pairs for entity .sync props
    get localStore(): Store {
        return this.store;
    }
    set localStore(val: Store) {
        this.$emit("update:store", val);
    }

    get localPatient(): Patient {
        return this.patient;
    }
    set localPatient(val: Patient) {
        this.$emit("update:patient", val);
    }

    get localDoctor(): Prescriber {
        return this.doctor;
    }
    set localDoctor(val: Prescriber) {
        this.$emit("update:doctor", val);
    }

    get showSupervisingPhysician(): boolean {
        return !!this.enableSupervisingPhysician && !!this.localDoctor?.requiresSupervisingPhysician;
    }

    get localSupervisingPhysicianId(): number | null | undefined {
        return this.formState.prescription?.supervisingPhysicianId;
    }

    get localSupervisingPhysician(): Prescriber | null {
        return this.supervisingPhysician;
    }

    set localSupervisingPhysician(value: Prescriber | null) {
        this.$emit("update:supervisingPhysician", value);
        if (this.formState.prescription) {
            this.formState.prescription.supervisingPhysicianId = value?.id ?? null;
        }
    }

    supervisingPhysicianOptionLabel(option: Prescriber): string {
        if (!option) return "";
        const lastName = option.lastName?.trim() ?? "";
        const firstName = option.firstName?.trim() ?? "";
        const name = [lastName, firstName].filter(Boolean).join(", ");
        return option.id ? `ID: ${option.id} ${name}` : name;
    }

    // Resolve the effective Account Manager for the current doctor + program.
    // Program-level overrides win over the doctor's default assignment.
    get effectiveAccountManagerName(): string | null {
        const doc = this.localDoctor;
        if (!doc) return null;

        const programId = this.localProgram?.id;
        if (programId) {
            const override = doc.programAccountManagers?.find((o) => o.programId === programId);
            if (override) {
                const name = `${override.accountManagerFirstName ?? ""} ${override.accountManagerLastName ?? ""}`.trim();
                return name || override.accountManagerUserId || null;
            }
        }

        if (!doc.accountManagerUserId) return null;
        const fallbackName = `${doc.accountManagerFirstName ?? ""} ${doc.accountManagerLastName ?? ""}`.trim();
        return fallbackName || doc.accountManagerUserId;
    }

    get localProgram(): Program {
        return this.program;
    }
    set localProgram(val: Program) {
        this.$emit("update:program", val);
    }

    get localDrug(): DrugProduct {
        return this.drug;
    }
    set localDrug(val: DrugProduct) {
        this.$emit("update:drug", val);
    }

    // Computed get/set pairs for primitive .sync props
    get localOriginCode(): number {
        return this.originCode;
    }
    set localOriginCode(val: number) {
        this.$emit("update:originCode", val);
    }

    get localStatusCode(): number {
        return this.statusCode;
    }
    set localStatusCode(val: number) {
        this.$emit("update:statusCode", val);
    }

    get localDeliveryCode(): number {
        return this.deliveryCode;
    }
    set localDeliveryCode(val: number) {
        this.$emit("update:deliveryCode", val);
    }

    get localDawCode(): number {
        return this.dawCode;
    }
    set localDawCode(val: number) {
        this.$emit("update:dawCode", val);
    }

    get localSubstatusCode(): number | null {
        return this.substatusCode;
    }
    set localSubstatusCode(val: number | null) {
        this.$emit("update:substatusCode", val);
    }

    get localTreatmentTypeCode(): number | null {
        return this.treatmentTypeCode;
    }
    set localTreatmentTypeCode(val: number | null) {
        this.$emit("update:treatmentTypeCode", val);
    }

    get localDispensedQuantity(): number {
        return this.dispensedQuantity;
    }
    set localDispensedQuantity(val: number) {
        this.$emit("update:dispensedQuantity", val);
    }

    get localExpiration(): Date | null {
        return this.expiration;
    }
    set localExpiration(val: Date | null) {
        this.$emit("update:expiration", val);
    }

    get localWrittenDate(): Date | any {
        return this.writtenDate;
    }
    set localWrittenDate(val: Date | any) {
        this.$emit("update:writtenDate", val);
    }

    get localNextTouchDateLocal(): string {
        return this.nextTouchDateLocal;
    }
    set localNextTouchDateLocal(val: string) {
        this.$emit("update:nextTouchDateLocal", val);
    }

    get localNextTouchDateManualOverride(): boolean {
        return this.nextTouchDateManualOverride;
    }
    set localNextTouchDateManualOverride(val: boolean) {
        this.$emit("update:nextTouchDateManualOverride", val);
    }

    get isCompact(): boolean {
        return this.layout === "compact";
    }

    private showCompactIngredients = false;

    get isCompoundDrug(): boolean {
        return !!(this.formState.prescription?.drugSource === 2
            && this.formState.drugDetails?.ingredients?.length
            && this.localDrug?.drugId);
    }
}
</script>

<style scoped lang="scss">
.compact-layout {
    font-size: 0.95em;

    ::v-deep(input),
    ::v-deep(select),
    ::v-deep(.vs__selected),
    ::v-deep(.vs__search),
    ::v-deep(.vs__dropdown-menu),
    ::v-deep(label),
    ::v-deep(.form-control) {
        font-size: inherit;
    }
}

.compact-search-label {
    display: flex;
    align-items: center;
    margin-bottom: 0;
    gap: 4px;

    label {
        font-weight: normal;
        margin-bottom: 0;
        margin-right: auto;

        .text-danger {
            color: inherit !important;
        }
    }
}

.compact-action-link {
    font-size: 0.9em;
    margin-left: 6px;
}

.compact-tight-spacing {
    ::v-deep(.form-group) {
        margin-bottom: 0.25rem;
    }
}

.compact-layout {
    ::v-deep(.directions) {
        min-height: unset;
        line-height: 1.33;
    }

    ::v-deep(.textarea-autosize) {
        max-height: 150px !important;
        line-height: 1.33;
    }

    .dispense-items ::v-deep {
        th, td {
            padding: 0.25rem 0.4rem;
            font-size: inherit;
        }
    }

}

.layout-fade-enter-active,
.layout-fade-leave-active {
    transition: opacity 0.15s ease;
}

.layout-fade-enter,
.layout-fade-leave-to {
    opacity: 0;
}
</style>

<style lang="scss">
// Non-scoped: BootstrapVue teleports the popover element to <body>, so scoped hashes don't match.
.compact-details-popover.popover {
    max-width: min(560px, calc(100vw - 32px));
}

.compact-details-popover .popover-body {
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
}
</style>
